import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: 'nz-calendar[appCalendarKeyboardNavigation]',
})
export class CalendarKeyboardNavigationDirective implements AfterViewInit, OnDestroy {
  private observer?: MutationObserver;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    this.updateTabStops();
    this.observer = new MutationObserver(() => this.updateTabStops());
    this.observer.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    const cell = (event.target as HTMLElement).closest<HTMLElement>('td.ant-picker-cell:not(.ant-picker-cell-disabled)');
    if (!cell) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      cell.click();
      return;
    }

    const cellOffset: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: -Infinity,
      End: Infinity,
    };
    const offset = cellOffset[event.key];
    if (offset === undefined) {
      return;
    }

    const cells = this.getCells();
    const currentIndex = cells.indexOf(cell);
    const nextIndex = offset === -Infinity ? 0 : offset === Infinity ? cells.length - 1 : Math.max(0, Math.min(currentIndex + offset, cells.length - 1));
    const nextCell = cells[nextIndex];
    if (!nextCell) {
      return;
    }

    event.preventDefault();
    this.setActiveCell(nextCell);
  }

  private updateTabStops(): void {
    const cells = this.getCells();
    if (!cells.length || cells.some((cell) => cell === document.activeElement)) {
      return;
    }

    const selectedCell = this.elementRef.nativeElement.querySelector<HTMLElement>('td.ant-picker-cell-selected:not(.ant-picker-cell-disabled)');
    this.setActiveCell(selectedCell ?? cells[0], false);
  }

  private getCells(): HTMLElement[] {
    return Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLElement>('td.ant-picker-cell:not(.ant-picker-cell-disabled)'));
  }

  private setActiveCell(activeCell: HTMLElement, focus = true): void {
    this.getCells().forEach((cell) => this.renderer.setAttribute(cell, 'tabindex', cell === activeCell ? '0' : '-1'));
    if (focus) {
      activeCell.focus();
    }
  }
}